import { useRef, useEffect, useState } from "react";
import { Badge, Box, Button, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import dayjs from "dayjs";
import { getLeadTimelineBadgeProps, getLeadTimelineEventDescription, getLeadTimelineIcon } from "./leadTimelineHelpers";

const LeadTimelineTab = ({ timeline, isLoading, onAddEntry, highlightId, onHighlightClear }) => {
    const itemRefs = useRef({});
    const [activeHighlight, setActiveHighlight] = useState(highlightId ?? null);

    useEffect(() => {
        if (!activeHighlight) return;
        const el = itemRefs.current[activeHighlight];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [timeline, activeHighlight]);

    useEffect(() => {
        if (!activeHighlight) return;
        const timer = setTimeout(() => { setActiveHighlight(null); onHighlightClear?.(); }, 2500);
        return () => clearTimeout(timer);
    }, [activeHighlight]);

    return (
        <Box>
            <HStack justify="space-between" mb={4}>
                <Text color="gray.500" fontSize="sm">{!isLoading && `Zdarzenia: ${timeline.length}`}</Text>
                <Button variant="outlined" size="sm" onClick={onAddEntry}>Dodaj wpis</Button>
            </HStack>
            {isLoading ? (
                <VStack py={8} align="center"><Spinner color="purple.400" size="sm" /></VStack>
            ) : timeline.length === 0 ? (
                <VStack py={10} align="center"><Text color="gray.500" fontSize="sm">Brak zdarzeń</Text></VStack>
            ) : (
                <VStack gap={0} align="stretch">
                    {timeline.map((event, i) => {
                        const badge = getLeadTimelineBadgeProps(event.kind);
                        const description = getLeadTimelineEventDescription(event);
                        const isLast = i === timeline.length - 1;
                        return (
                            <HStack key={event.id} ref={el => { itemRefs.current[event.id] = el; }} align="stretch" gap={4}>
                                <VStack gap={0} align="center" flexShrink={0} w="36px">
                                    <Box
                                        w="36px" h="36px" borderRadius="full" bg="gray.50" border="1px solid" borderColor="gray.200"
                                        display="flex" alignItems="center" justifyContent="center" color="gray.500" fontSize="sm" flexShrink={0}
                                    >
                                        {getLeadTimelineIcon(event.kind)}
                                    </Box>
                                    {!isLast && <Box w="1px" bg="gray.200" flex={1} minH="20px" />}
                                </VStack>
                                <Box flex={1} pb={isLast ? 0 : 6}>
                                    <Box
                                        bg="white" border="1px solid"
                                        borderColor={activeHighlight === event.id ? "purple.400" : "gray.200"}
                                        borderRadius={10} p={4} transition="border-color 0.5s"
                                    >
                                        <HStack justify="space-between" mb={description ? 2 : 0} flexWrap="wrap" gap={1}>
                                            <HStack gap={2}>
                                                <Badge variant="subtle" colorPalette={badge.colorPalette} borderRadius={6} fontSize="xs" fontWeight={600}>{badge.label}</Badge>
                                                <Text color="gray.500" fontSize="xs">{event.authorFullName ?? "System"}</Text>
                                            </HStack>
                                            <Text color="gray.500" fontSize="xs">{dayjs(event.occurredAt).format("DD.MM.YYYY HH:mm")}</Text>
                                        </HStack>
                                        {description && <Text color="gray.900" fontSize="sm">{description}</Text>}
                                    </Box>
                                </Box>
                            </HStack>
                        );
                    })}
                </VStack>
            )}
        </Box>
    );
};

export default LeadTimelineTab;
